import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Tab,
    TabList,
    SelectTabEvent,
    SelectTabData,
    Text,
    Button,
    Spinner,
    Badge,
    Switch,
    Card,
    Input,
} from '@fluentui/react-components';
import {
    ArrowLeft24Regular,
    People24Regular,
    PlugConnected24Regular,
    Bot24Regular,
    Library24Regular,
    Settings24Regular,
    Checkmark20Filled,
    ShieldLock24Regular,
    ClipboardDataBar24Regular,
} from '@fluentui/react-icons';
import { TeamConfig } from '../models/Team';
import { TeamService } from '../services/TeamService';
import styles from '../styles/Settings.module.css';

type SettingsTab = 'teams' | 'integrations' | 'models' | 'knowledge' | 'advanced' | 'security' | 'audit';

// Integration configuration
interface Integration {
    id: string;
    name: string;
    category: string;
    description: string;
    icon: string;
    enabled: boolean;
    status: 'connected' | 'disconnected' | 'error';
}

// Model configuration
interface ModelConfig {
    id: string;
    name: string;
    provider: string;
    category: string;
    description: string;
    icon: string;
    isAvailable: boolean;
    assignedTo: string[];
}

const SettingsPage: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<SettingsTab>('teams');
    
    // Teams state
    const [teams, setTeams] = useState<TeamConfig[]>([]);
    const [selectedTeam, setSelectedTeam] = useState<TeamConfig | null>(null);
    const [teamsLoading, setTeamsLoading] = useState(false);

    // Integrations state
    const [integrations, setIntegrations] = useState<Integration[]>([
        // ERP Systems
        {
            id: 'oracle-fusion',
            name: 'Oracle Fusion',
            category: 'ERP Systems',
            description: 'Enterprise resource planning for financials, procurement, and supply chain',
            icon: 'https://img.icons8.com/color/96/oracle-logo.png',
            enabled: true,
            status: 'connected'
        },
        {
            id: 'dynamics-365',
            name: 'Microsoft Dynamics 365',
            category: 'ERP Systems',
            description: 'Business applications for finance, operations, and customer engagement',
            icon: 'https://img.icons8.com/color/96/microsoft.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'sap',
            name: 'SAP S/4HANA',
            category: 'ERP Systems',
            description: 'Intelligent ERP for real-time business operations',
            icon: 'https://img.icons8.com/color/96/sap.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'netsuite',
            name: 'Oracle NetSuite',
            category: 'ERP Systems',
            description: 'Cloud-based ERP for growing businesses',
            icon: 'https://img.icons8.com/color/96/cloud.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'workday',
            name: 'Workday Financials',
            category: 'ERP Systems',
            description: 'Enterprise cloud applications for finance and HR',
            icon: 'https://img.icons8.com/color/96/workday.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'sage-intacct',
            name: 'Sage Intacct',
            category: 'ERP Systems',
            description: 'Cloud financial management software',
            icon: 'https://img.icons8.com/color/96/accounting.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'infor',
            name: 'Infor CloudSuite',
            category: 'ERP Systems',
            description: 'Industry-specific cloud ERP solutions',
            icon: 'https://img.icons8.com/color/96/cloud-storage.png',
            enabled: false,
            status: 'disconnected'
        },
        // Vendor Management
        {
            id: 'paymentworks',
            name: 'PaymentWorks',
            category: 'Vendor Management',
            description: 'Vendor onboarding and payment security platform',
            icon: 'https://img.icons8.com/color/96/bank-card-back-side.png',
            enabled: true,
            status: 'connected'
        },
        {
            id: 'ariba',
            name: 'SAP Ariba',
            category: 'Vendor Management',
            description: 'Procurement and supply chain collaboration',
            icon: 'https://img.icons8.com/color/96/handshake.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'jaggaer',
            name: 'Jaggaer',
            category: 'Vendor Management',
            description: 'Autonomous commerce platform for procurement',
            icon: 'https://img.icons8.com/color/96/supplier.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'gep',
            name: 'GEP SMART',
            category: 'Vendor Management',
            description: 'Unified procurement platform',
            icon: 'https://img.icons8.com/color/96/purchase-order.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'ivalua',
            name: 'Ivalua',
            category: 'Vendor Management',
            description: 'Spend management and procurement solutions',
            icon: 'https://img.icons8.com/color/96/money-bag.png',
            enabled: false,
            status: 'disconnected'
        },
        // AP Automation
        {
            id: 'bill-com',
            name: 'Bill.com',
            category: 'AP Automation',
            description: 'Accounts payable and receivable automation',
            icon: 'https://img.icons8.com/color/96/bill.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'tipalti',
            name: 'Tipalti',
            category: 'AP Automation',
            description: 'Global payables automation and mass payments',
            icon: 'https://img.icons8.com/color/96/globe--v1.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'airbase',
            name: 'Airbase',
            category: 'AP Automation',
            description: 'All-in-one spend management platform',
            icon: 'https://img.icons8.com/color/96/airplane-take-off.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'stampli',
            name: 'Stampli',
            category: 'AP Automation',
            description: 'AI-powered accounts payable automation',
            icon: 'https://img.icons8.com/color/96/postage-stamp.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'medius',
            name: 'Medius',
            category: 'AP Automation',
            description: 'Autonomous AP and spend management',
            icon: 'https://img.icons8.com/color/96/automation.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'mineraltree',
            name: 'MineralTree',
            category: 'AP Automation',
            description: 'Invoice-to-pay automation platform',
            icon: 'https://img.icons8.com/color/96/tree.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'yooz',
            name: 'Yooz',
            category: 'AP Automation',
            description: 'Cloud-based purchase-to-pay automation',
            icon: 'https://img.icons8.com/color/96/rocket.png',
            enabled: false,
            status: 'disconnected'
        },
        // Document Processing
        {
            id: 'nimbello',
            name: 'Nimbello',
            category: 'Document Processing',
            description: 'Invoice intake and OCR processing',
            icon: 'https://img.icons8.com/color/96/document--v1.png',
            enabled: true,
            status: 'connected'
        },
        {
            id: 'rossum',
            name: 'Rossum',
            category: 'Document Processing',
            description: 'AI-powered document understanding and data extraction',
            icon: 'https://img.icons8.com/color/96/artificial-intelligence.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'abbyy',
            name: 'ABBYY Vantage',
            category: 'Document Processing',
            description: 'Intelligent document processing platform',
            icon: 'https://img.icons8.com/color/96/scanner.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'kofax',
            name: 'Kofax',
            category: 'Document Processing',
            description: 'Intelligent automation for document workflows',
            icon: 'https://img.icons8.com/color/96/workflow.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'hyperscience',
            name: 'Hyperscience',
            category: 'Document Processing',
            description: 'Machine learning document automation',
            icon: 'https://img.icons8.com/color/96/brain.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'docsumo',
            name: 'Docsumo',
            category: 'Document Processing',
            description: 'AI document data capture',
            icon: 'https://img.icons8.com/color/96/document-scanner.png',
            enabled: false,
            status: 'disconnected'
        },
        // Procurement
        {
            id: 'coupa',
            name: 'Coupa',
            category: 'Procurement',
            description: 'Business spend management platform',
            icon: 'https://img.icons8.com/color/96/shopping-cart--v1.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'procurify',
            name: 'Procurify',
            category: 'Procurement',
            description: 'Real-time spend management',
            icon: 'https://img.icons8.com/color/96/purchase-order.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'precoro',
            name: 'Precoro',
            category: 'Procurement',
            description: 'Procurement and spend management software',
            icon: 'https://img.icons8.com/color/96/shopping-basket.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'kissflow',
            name: 'Kissflow Procurement',
            category: 'Procurement',
            description: 'End-to-end procurement cloud',
            icon: 'https://img.icons8.com/color/96/approval.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'tradogram',
            name: 'Tradogram',
            category: 'Procurement',
            description: 'Smart procurement software',
            icon: 'https://img.icons8.com/color/96/combo-chart.png',
            enabled: false,
            status: 'disconnected'
        },
        // Banking & Payments
        {
            id: 'plaid',
            name: 'Plaid',
            category: 'Banking & Payments',
            description: 'Financial data connectivity',
            icon: 'https://img.icons8.com/color/96/bank-building.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'stripe',
            name: 'Stripe',
            category: 'Banking & Payments',
            description: 'Payment processing infrastructure',
            icon: 'https://img.icons8.com/color/96/stripe.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'jpmorgan',
            name: 'J.P. Morgan Access',
            category: 'Banking & Payments',
            description: 'Treasury and payment services',
            icon: 'https://img.icons8.com/color/96/bank.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'bofa',
            name: 'Bank of America CashPro',
            category: 'Banking & Payments',
            description: 'Treasury management platform',
            icon: 'https://img.icons8.com/color/96/money-transfer.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'wells-fargo',
            name: 'Wells Fargo CEO',
            category: 'Banking & Payments',
            description: 'Commercial electronic office',
            icon: 'https://img.icons8.com/color/96/safe.png',
            enabled: false,
            status: 'disconnected'
        },
        // Sales & Order Management
        {
            id: 'jira',
            name: 'Jira',
            category: 'Sales & Order Management',
            description: 'Project tracking and issue management for order operations',
            icon: 'https://img.icons8.com/color/96/jira.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'salesforce',
            name: 'Salesforce',
            category: 'Sales & Order Management',
            description: 'CRM platform for customer and opportunity management',
            icon: 'https://img.icons8.com/color/96/salesforce.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'netsuite-orders',
            name: 'NetSuite Orders',
            category: 'Sales & Order Management',
            description: 'Sales order management and credit memo processing',
            icon: 'https://img.icons8.com/color/96/oracle-logo.png',
            enabled: false,
            status: 'disconnected'
        },
        {
            id: 'slack',
            name: 'Slack',
            category: 'Sales & Order Management',
            description: 'Team messaging and notifications for order workflows',
            icon: 'https://img.icons8.com/color/96/slack-new.png',
            enabled: false,
            status: 'disconnected'
        },
    ]);

    // Track expanded categories
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            return newSet;
        });
    };

    // Models state
    const [models, setModels] = useState<ModelConfig[]>([
        // Nolij Models (Custom Finance Models)
        {
            id: 'nolij-sage',
            name: 'Nolij-Sage',
            provider: 'Nolij',
            category: 'Nolij Models',
            description: 'Fine-tuned for financial planning and strategic analysis',
            icon: 'https://img.icons8.com/color/96/crystal-ball.png',
            isAvailable: true,
            assignedTo: ['Systems Analyst']
        },
        {
            id: 'nolij-logic',
            name: 'Nolij-Logic',
            provider: 'Nolij',
            category: 'Nolij Models',
            description: 'Optimized for system integration and data orchestration',
            icon: 'https://img.icons8.com/color/96/light-on.png',
            isAvailable: true,
            assignedTo: ['Research Analyst']
        },
        {
            id: 'nolij-analyzer',
            name: 'Nolij-Analyzer',
            provider: 'Nolij',
            category: 'Nolij Models',
            description: 'Specialized for complex financial analysis and reasoning',
            icon: 'https://img.icons8.com/color/96/microscope.png',
            isAvailable: true,
            assignedTo: ['Senior Financial Analyst']
        },
        // Reasoning Models
        {
            id: 'deepseek-r1',
            name: 'DeepSeek R1',
            provider: 'DeepSeek',
            category: 'Reasoning Models',
            description: 'Advanced reasoning model with chain-of-thought capabilities',
            icon: 'https://img.icons8.com/color/96/brain.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'o1',
            name: 'OpenAI o1',
            provider: 'OpenAI',
            category: 'Reasoning Models',
            description: 'Advanced reasoning for complex problem solving',
            icon: 'https://img.icons8.com/color/96/chatgpt.png',
            isAvailable: false,
            assignedTo: []
        },
        {
            id: 'o1-mini',
            name: 'OpenAI o1-mini',
            provider: 'OpenAI',
            category: 'Reasoning Models',
            description: 'Faster reasoning model for everyday tasks',
            icon: 'https://img.icons8.com/color/96/chatgpt.png',
            isAvailable: false,
            assignedTo: []
        },
        {
            id: 'qwen-qwq',
            name: 'Qwen QwQ',
            provider: 'Alibaba',
            category: 'Reasoning Models',
            description: 'Open-source reasoning model with strong math capabilities',
            icon: 'https://img.icons8.com/color/96/artificial-intelligence.png',
            isAvailable: true,
            assignedTo: []
        },
        // Small & Fast Models
        {
            id: 'llama3.2-3b',
            name: 'Llama 3.2 3B',
            provider: 'Meta',
            category: 'Small & Fast Models',
            description: 'Lightweight model for quick responses and edge deployment',
            icon: 'https://img.icons8.com/color/96/llama.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'phi-3-mini',
            name: 'Phi-3 Mini',
            provider: 'Microsoft',
            category: 'Small & Fast Models',
            description: 'Compact model with strong reasoning for its size',
            icon: 'https://img.icons8.com/color/96/microsoft.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'gemma-2b',
            name: 'Gemma 2B',
            provider: 'Google',
            category: 'Small & Fast Models',
            description: 'Lightweight open model from Google',
            icon: 'https://img.icons8.com/color/96/google-logo.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'mistral-7b',
            name: 'Mistral 7B',
            provider: 'Mistral AI',
            category: 'Small & Fast Models',
            description: 'Efficient model for quick responses',
            icon: 'https://img.icons8.com/color/96/wind.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'qwen2.5-7b',
            name: 'Qwen 2.5 7B',
            provider: 'Alibaba',
            category: 'Small & Fast Models',
            description: 'Fast multilingual model with strong coding abilities',
            icon: 'https://img.icons8.com/color/96/artificial-intelligence.png',
            isAvailable: true,
            assignedTo: []
        },
        // Large Open Source Models
        {
            id: 'llama3.1-70b',
            name: 'Llama 3.1 70B',
            provider: 'Meta',
            category: 'Large Open Source Models',
            description: 'Powerful open model for complex tasks',
            icon: 'https://img.icons8.com/color/96/llama.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'qwen2.5-14b',
            name: 'Qwen 2.5 14B',
            provider: 'Alibaba',
            category: 'Large Open Source Models',
            description: 'Strong reasoning and code generation capabilities',
            icon: 'https://img.icons8.com/color/96/artificial-intelligence.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'qwen2.5-72b',
            name: 'Qwen 2.5 72B',
            provider: 'Alibaba',
            category: 'Large Open Source Models',
            description: 'State-of-the-art open model with broad capabilities',
            icon: 'https://img.icons8.com/color/96/artificial-intelligence.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'mixtral-8x22b',
            name: 'Mixtral 8x22B',
            provider: 'Mistral AI',
            category: 'Large Open Source Models',
            description: 'Mixture of experts model for diverse tasks',
            icon: 'https://img.icons8.com/color/96/wind.png',
            isAvailable: false,
            assignedTo: []
        },
        {
            id: 'deepseek-v3',
            name: 'DeepSeek V3',
            provider: 'DeepSeek',
            category: 'Large Open Source Models',
            description: 'Latest generation open model with strong performance',
            icon: 'https://img.icons8.com/color/96/brain.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'command-r-plus',
            name: 'Command R+',
            provider: 'Cohere',
            category: 'Large Open Source Models',
            description: 'Enterprise-grade model optimized for RAG',
            icon: 'https://img.icons8.com/color/96/command.png',
            isAvailable: false,
            assignedTo: []
        },
        // Cloud Provider Models
        {
            id: 'gpt-4o',
            name: 'GPT-4o',
            provider: 'OpenAI',
            category: 'Cloud Provider Models',
            description: 'Multimodal flagship model from OpenAI',
            icon: 'https://img.icons8.com/color/96/chatgpt.png',
            isAvailable: false,
            assignedTo: []
        },
        {
            id: 'gpt-4o-mini',
            name: 'GPT-4o Mini',
            provider: 'OpenAI',
            category: 'Cloud Provider Models',
            description: 'Cost-effective model for everyday tasks',
            icon: 'https://img.icons8.com/color/96/chatgpt.png',
            isAvailable: false,
            assignedTo: []
        },
        {
            id: 'claude-3.5-sonnet',
            name: 'Claude 3.5 Sonnet',
            provider: 'Anthropic',
            category: 'Cloud Provider Models',
            description: 'Balanced model with strong coding and analysis',
            icon: 'https://img.icons8.com/color/96/bot.png',
            isAvailable: false,
            assignedTo: []
        },
        {
            id: 'claude-3-opus',
            name: 'Claude 3 Opus',
            provider: 'Anthropic',
            category: 'Cloud Provider Models',
            description: 'Most capable Claude model for complex tasks',
            icon: 'https://img.icons8.com/color/96/bot.png',
            isAvailable: false,
            assignedTo: []
        },
        {
            id: 'gemini-2.0-flash',
            name: 'Gemini 2.0 Flash',
            provider: 'Google',
            category: 'Cloud Provider Models',
            description: 'Fast multimodal model with agentic capabilities',
            icon: 'https://img.icons8.com/color/96/google-logo.png',
            isAvailable: false,
            assignedTo: []
        },
        {
            id: 'gemini-1.5-pro',
            name: 'Gemini 1.5 Pro',
            provider: 'Google',
            category: 'Cloud Provider Models',
            description: 'Long context model with 1M token window',
            icon: 'https://img.icons8.com/color/96/google-logo.png',
            isAvailable: false,
            assignedTo: []
        },
        // Specialized Models
        {
            id: 'codellama-34b',
            name: 'Code Llama 34B',
            provider: 'Meta',
            category: 'Specialized Models',
            description: 'Optimized for code generation and understanding',
            icon: 'https://img.icons8.com/color/96/source-code.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'starcoder2',
            name: 'StarCoder2 15B',
            provider: 'BigCode',
            category: 'Specialized Models',
            description: 'Open code model trained on permissive data',
            icon: 'https://img.icons8.com/color/96/star.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'nomic-embed',
            name: 'Nomic Embed',
            provider: 'Nomic AI',
            category: 'Specialized Models',
            description: 'High-quality text embeddings for RAG',
            icon: 'https://img.icons8.com/color/96/vector.png',
            isAvailable: true,
            assignedTo: ['Research Analyst']
        },
        {
            id: 'whisper-large',
            name: 'Whisper Large V3',
            provider: 'OpenAI',
            category: 'Specialized Models',
            description: 'Speech recognition and transcription',
            icon: 'https://img.icons8.com/color/96/microphone.png',
            isAvailable: true,
            assignedTo: []
        },
        {
            id: 'llava',
            name: 'LLaVA 1.6',
            provider: 'Microsoft',
            category: 'Specialized Models',
            description: 'Vision-language model for image understanding',
            icon: 'https://img.icons8.com/color/96/visible.png',
            isAvailable: true,
            assignedTo: []
        },
    ]);

    // Track expanded model categories
    const [expandedModelCategories, setExpandedModelCategories] = useState<Set<string>>(new Set());

    const toggleModelCategory = (category: string) => {
        setExpandedModelCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            return newSet;
        });
    };

    // Knowledge base state
    const [knowledgeBases, setKnowledgeBases] = useState([
        // Internal Document Repositories
        {
            id: 'sharepoint',
            name: 'SharePoint',
            category: 'Internal Document Repositories',
            description: 'Corporate document management and collaboration platform',
            icon: 'https://img.icons8.com/color/96/sharepoint.png',
            documentCount: 15420,
            lastUpdated: '2025-01-16',
            status: 'active'
        },
        {
            id: 'confluence',
            name: 'Confluence Wiki',
            category: 'Internal Document Repositories',
            description: 'Team knowledge base and documentation wiki',
            icon: 'https://img.icons8.com/color/96/confluence.png',
            documentCount: 3250,
            lastUpdated: '2025-01-15',
            status: 'active'
        },
        {
            id: 'google-drive',
            name: 'Google Drive',
            category: 'Internal Document Repositories',
            description: 'Cloud storage and document collaboration',
            icon: 'https://img.icons8.com/color/96/google-drive.png',
            documentCount: 8900,
            lastUpdated: '2025-01-16',
            status: 'active'
        },
        {
            id: 'onedrive',
            name: 'OneDrive for Business',
            category: 'Internal Document Repositories',
            description: 'Microsoft cloud storage and file sharing',
            icon: 'https://img.icons8.com/color/96/onedrive.png',
            documentCount: 6200,
            lastUpdated: '2025-01-15',
            status: 'active'
        },
        {
            id: 'notion',
            name: 'Notion',
            category: 'Internal Document Repositories',
            description: 'All-in-one workspace for notes and documentation',
            icon: 'https://img.icons8.com/color/96/notion.png',
            documentCount: 1850,
            lastUpdated: '2025-01-14',
            status: 'inactive'
        },
        // Financial Databases
        {
            id: 'vendor_database',
            name: 'Vendor Database',
            category: 'Financial Databases',
            description: 'Vendor information, contracts, and payment history',
            icon: 'https://img.icons8.com/color/96/supplier.png',
            documentCount: 1250,
            lastUpdated: '2025-01-14',
            status: 'active'
        },
        {
            id: 'contracts_database',
            name: 'Contracts Database',
            category: 'Financial Databases',
            description: 'Contract terms, SLAs, and compliance documents',
            icon: 'https://img.icons8.com/color/96/contract.png',
            documentCount: 450,
            lastUpdated: '2025-01-13',
            status: 'active'
        },
        {
            id: 'invoices_database',
            name: 'Invoice Archive',
            category: 'Financial Databases',
            description: 'Historical invoices and payment records',
            icon: 'https://img.icons8.com/color/96/bill.png',
            documentCount: 28500,
            lastUpdated: '2025-01-16',
            status: 'active'
        },
        {
            id: 'grants_database',
            name: 'Grants Database',
            category: 'Financial Databases',
            description: 'Grant information and funding sources',
            icon: 'https://img.icons8.com/color/96/money-bag.png',
            documentCount: 320,
            lastUpdated: '2025-01-12',
            status: 'active'
        },
        // Policy & Compliance
        {
            id: 'policies_database',
            name: 'Policies Database',
            category: 'Policy & Compliance',
            description: 'Company policies, procedures, and guidelines',
            icon: 'https://img.icons8.com/color/96/policy-document.png',
            documentCount: 180,
            lastUpdated: '2025-01-10',
            status: 'active'
        },
        {
            id: 'compliance_docs',
            name: 'Compliance Documents',
            category: 'Policy & Compliance',
            description: 'Regulatory compliance and audit documentation',
            icon: 'https://img.icons8.com/color/96/checkmark.png',
            documentCount: 520,
            lastUpdated: '2025-01-11',
            status: 'active'
        },
        {
            id: 'sox_controls',
            name: 'SOX Controls Library',
            category: 'Policy & Compliance',
            description: 'Sarbanes-Oxley compliance controls and evidence',
            icon: 'https://img.icons8.com/color/96/shield.png',
            documentCount: 340,
            lastUpdated: '2025-01-09',
            status: 'active'
        },
        // External Sources
        {
            id: 'sec_filings',
            name: 'SEC Filings',
            category: 'External Sources',
            description: 'Public company filings and financial reports',
            icon: 'https://img.icons8.com/color/96/government.png',
            documentCount: 12000,
            lastUpdated: '2025-01-16',
            status: 'active'
        },
        {
            id: 'industry_reports',
            name: 'Industry Reports',
            category: 'External Sources',
            description: 'Market research and industry analysis',
            icon: 'https://img.icons8.com/color/96/combo-chart.png',
            documentCount: 850,
            lastUpdated: '2025-01-08',
            status: 'active'
        },
        {
            id: 'news_feeds',
            name: 'News & Alerts',
            category: 'External Sources',
            description: 'Financial news and vendor alerts',
            icon: 'https://img.icons8.com/color/96/news.png',
            documentCount: 45000,
            lastUpdated: '2025-01-16',
            status: 'active'
        },
    ]);

    // Track expanded knowledge categories
    const [expandedKnowledgeCategories, setExpandedKnowledgeCategories] = useState<Set<string>>(new Set());

    const toggleKnowledgeCategory = (category: string) => {
        setExpandedKnowledgeCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            return newSet;
        });
    };

    // Load teams on mount
    useEffect(() => {
        loadTeams();
    }, []);

    const loadTeams = async () => {
        setTeamsLoading(true);
        try {
            const teamsData = await TeamService.getUserTeams();
            setTeams(teamsData);
            
            // Get stored team
            const storedTeam = TeamService.getStoredTeam();
            if (storedTeam) {
                const matchingTeam = teamsData.find(t => 
                    (t.team_id || t.id) === (storedTeam.team_id || storedTeam.id)
                );
                if (matchingTeam) {
                    setSelectedTeam(matchingTeam);
                }
            }
        } catch (error) {
            console.error('Failed to load teams:', error);
        } finally {
            setTeamsLoading(false);
        }
    };

    const handleTeamSelect = async (team: TeamConfig) => {
        setSelectedTeam(team);
        TeamService.storageTeam(team);
        
        // Call backend to set team
        try {
            await TeamService.selectTeam(team.team_id || team.id);
        } catch (error) {
            console.error('Failed to select team:', error);
        }
    };

    const handleIntegrationToggle = (integrationId: string) => {
        setIntegrations(prev => prev.map(integration => {
            if (integration.id === integrationId) {
                const newEnabled = !integration.enabled;
                return {
                    ...integration,
                    enabled: newEnabled,
                    status: newEnabled ? 'connected' : 'disconnected'
                };
            }
            return integration;
        }));
    };

    const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
        setActiveTab(data.value as SettingsTab);
    };

    const handleBack = () => {
        navigate('/');
    };

    // Team icons mapping based on team name/id
    const getTeamIcon = (teamId: string, teamName: string): string => {
        const iconMap: { [key: string]: string } = {
            'ap_operations_team': '💼',
            'ar_operations_team': '💰',
            'gl_close_team': '📊',
            'treasury_team': '🏦',
            'procurement_team': '🛒',
            'fpa_team': '📈',
            'tax_compliance_team': '📋',
            'expense_management_team': '💳',
        };
        
        // Try to match by team_id first
        if (iconMap[teamId]) return iconMap[teamId];
        
        // Fallback based on name keywords
        const nameLower = teamName.toLowerCase();
        if (nameLower.includes('payable') || nameLower.includes('ap ')) return '💼';
        if (nameLower.includes('receivable') || nameLower.includes('ar ')) return '💰';
        if (nameLower.includes('ledger') || nameLower.includes('gl ')) return '📊';
        if (nameLower.includes('treasury') || nameLower.includes('cash')) return '🏦';
        if (nameLower.includes('procurement') || nameLower.includes('sourcing')) return '🛒';
        if (nameLower.includes('planning') || nameLower.includes('fpa')) return '📈';
        if (nameLower.includes('tax') || nameLower.includes('compliance')) return '📋';
        if (nameLower.includes('expense')) return '💳';
        
        return '👥'; // Default icon
    };

    // Agent icons mapping based on agent name/role
    const getAgentIcon = (agentName: string): string => {
        const nameLower = agentName.toLowerCase();
        
        // Specific manager types - check these BEFORE generic "manager"
        if (nameLower.includes('ap manager')) return '📥';
        if (nameLower.includes('ar manager')) return '📬';
        if (nameLower.includes('gl manager')) return '📒';
        if (nameLower.includes('treasury manager')) return '🏛️';
        if (nameLower.includes('fpa manager') || nameLower.includes('fp&a manager')) return '📊';
        if (nameLower.includes('expense manager')) return '💳';
        if (nameLower.includes('procurement manager')) return '🛒';
        if (nameLower.includes('tax manager')) return '🧾';
        if (nameLower.includes('manager')) return '👔'; // Generic manager fallback
        
        // Specific analyst types - check full names BEFORE partial matches
        if (nameLower.includes('research analyst')) return '🔬';
        if (nameLower.includes('systems analyst')) return '💻';
        if (nameLower.includes('senior financial analyst')) return '📊';
        if (nameLower.includes('collections analyst')) return '📞';
        if (nameLower.includes('customer research analyst')) return '🔍';
        if (nameLower.includes('budget analyst')) return '🎯';
        if (nameLower.includes('forecasting analyst')) return '🔮';
        if (nameLower.includes('business partner analyst')) return '🤝';
        if (nameLower.includes('regulatory research analyst')) return '📚';
        if (nameLower.includes('audit defense analyst')) return '🛡️';
        if (nameLower.includes('audit support analyst')) return '📖';
        if (nameLower.includes('policy compliance analyst')) return '✅';
        if (nameLower.includes('fraud detection analyst')) return '🚨';
        if (nameLower.includes('vendor evaluation analyst')) return '🏷️';
        if (nameLower.includes('close analyst')) return '📅';
        if (nameLower.includes('intercompany analyst')) return '🔄';
        if (nameLower.includes('reconciliation analyst')) return '⚖️';
        if (nameLower.includes('cash positioning analyst')) return '💵';
        if (nameLower.includes('investment analyst')) return '📈';
        if (nameLower.includes('risk analyst')) return '⚠️';
        if (nameLower.includes('sourcing analyst')) return '🔎';
        if (nameLower.includes('contract analyst')) return '📝';
        if (nameLower.includes('supplier analyst')) return '🏭';
        if (nameLower.includes('expense analyst')) return '🧳';
        if (nameLower.includes('policy analyst')) return '📜';
        if (nameLower.includes('financial analyst')) return '💹';
        if (nameLower.includes('credit analyst')) return '🏧';
        if (nameLower.includes('data analyst')) return '📉';
        if (nameLower.includes('business analyst')) return '📋';
        if (nameLower.includes('analyst')) return '🧮'; // Generic analyst fallback
        
        // Specialist roles
        if (nameLower.includes('cash application specialist')) return '🏦';
        if (nameLower.includes('tax calculation specialist')) return '🧮';
        if (nameLower.includes('collections specialist')) return '📱';
        if (nameLower.includes('specialist')) return '⭐';
        
        // Other roles
        if (nameLower.includes('accountant')) return '🧑‍💻';
        if (nameLower.includes('controller')) return '🎛️';
        if (nameLower.includes('auditor')) return '🔎';
        
        return '🤖'; // Default agent icon
    };

    // Render Teams Tab
    const renderTeamsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionHeader}>
                <h1 className={styles.sectionTitle}>Choose Your Team</h1>
                <p className={styles.sectionDescription}>
                    Select an AI agent team tailored to your workflow. Each team brings specialized expertise to help you get things done faster.
                </p>
            </div>
            
            {teamsLoading ? (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading teams..." />
                </div>
            ) : (
                <div className={styles.teamsGrid}>
                    {teams.map((team, index) => {
                        const teamId = team.team_id || team.id;
                        const selectedId = selectedTeam?.team_id || selectedTeam?.id;
                        const isSelected = teamId === selectedId;
                        const teamIcon = getTeamIcon(teamId, team.name);
                        
                        return (
                            <Card
                                key={teamId || index}
                                className={`${styles.teamCard} ${isSelected ? styles.teamCardSelected : ''}`}
                                onClick={() => handleTeamSelect(team)}
                            >
                                <div className={styles.teamCardHeader}>
                                    <div className={styles.teamTitleRow}>
                                        <span className={styles.teamIcon}>{teamIcon}</span>
                                        <Text className={styles.teamName}>{team.name}</Text>
                                    </div>
                                    {isSelected && (
                                        <Badge appearance="filled" color="brand" className={styles.selectedBadge}>
                                            <Checkmark20Filled /> Selected
                                        </Badge>
                                    )}
                                </div>
                                <Text className={styles.teamDescription}>{team.description}</Text>
                                <div className={styles.agentsSection}>
                                    <Text className={styles.agentsLabel}>Agents</Text>
                                    <div className={styles.agentsList}>
                                        {team.agents?.slice(0, 4).map((agent, agentIndex) => (
                                            <div key={agentIndex} className={styles.agentItem}>
                                                <span className={styles.agentIcon}>{getAgentIcon(agent.name)}</span>
                                                <span className={styles.agentName}>{agent.name}</span>
                                            </div>
                                        ))}
                                        {team.agents && team.agents.length > 4 && (
                                            <div className={styles.agentItem}>
                                                <span className={styles.agentIcon}>➕</span>
                                                <span className={styles.agentName}>{team.agents.length - 4} more</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );

    // Render Integrations Tab
    const renderIntegrationsTab = () => {
        const categories = [...new Set(integrations.map(i => i.category))];
        const ITEMS_TO_SHOW = 3;
        
        return (
            <div className={styles.tabContent}>
                <div className={styles.sectionHeader}>
                    <h1 className={styles.sectionTitle}>Connect Your Systems</h1>
                    <p className={styles.sectionDescription}>
                        Integrate with ERP systems, AP automation tools, and document processing services via MCP to unlock powerful workflows.
                    </p>
                </div>
                
                {categories.map(category => {
                    const categoryIntegrations = integrations.filter(i => i.category === category);
                    const isExpanded = expandedCategories.has(category);
                    const displayedIntegrations = isExpanded 
                        ? categoryIntegrations 
                        : categoryIntegrations.slice(0, ITEMS_TO_SHOW);
                    const hasMore = categoryIntegrations.length > ITEMS_TO_SHOW;
                    const remainingCount = categoryIntegrations.length - ITEMS_TO_SHOW;
                    
                    return (
                        <div key={category} className={styles.integrationCategory}>
                            <div className={styles.categoryHeader}>
                                <Text className={styles.categoryTitle}>{category}</Text>
                                <Text className={styles.categoryCount}>
                                    {categoryIntegrations.length} integrations available
                                </Text>
                            </div>
                            <div className={styles.integrationsGrid}>
                                {displayedIntegrations.map(integration => (
                                    <Card key={integration.id} className={styles.integrationCard}>
                                        <div className={styles.integrationHeader}>
                                            <img 
                                                src={integration.icon} 
                                                alt={integration.name}
                                                className={styles.integrationIcon}
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                }}
                                            />
                                            <div className={styles.integrationInfo}>
                                                <Text className={styles.integrationName}>{integration.name}</Text>
                                                <br />
                                                {integration.status === 'connected' && (
                                                    <Badge 
                                                        appearance="filled" 
                                                        color="success"
                                                        className={styles.statusBadge}
                                                    >
                                                        connected
                                                    </Badge>
                                                )}
                                            </div>
                                            <Switch
                                                checked={integration.enabled}
                                                onChange={() => handleIntegrationToggle(integration.id)}
                                            />
                                        </div>
                                        <Text className={styles.integrationDescription}>
                                            {integration.description}
                                        </Text>
                                    </Card>
                                ))}
                            </div>
                            {hasMore && (
                                <Button 
                                    appearance="subtle" 
                                    className={styles.showMoreButton}
                                    onClick={() => toggleCategory(category)}
                                >
                                    {isExpanded ? 'Show less' : `Show ${remainingCount} more integrations`}
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Render Models Tab
    const renderModelsTab = () => {
        const modelCategories = [...new Set(models.map(m => m.category))];
        const MODELS_TO_SHOW = 3;
        
        return (
            <div className={styles.tabContent}>
                <div className={styles.sectionHeader}>
                    <h1 className={styles.sectionTitle}>Choose Your AI Models</h1>
                    <p className={styles.sectionDescription}>
                        Select from a wide range of AI models to power your agents. Mix and match based on your needs for speed, accuracy, and cost.
                    </p>
                </div>
                
                {modelCategories.map(category => {
                    const categoryModels = models.filter(m => m.category === category);
                    const isExpanded = expandedModelCategories.has(category);
                    const displayedModels = isExpanded 
                        ? categoryModels 
                        : categoryModels.slice(0, MODELS_TO_SHOW);
                    const hasMore = categoryModels.length > MODELS_TO_SHOW;
                    const remainingCount = categoryModels.length - MODELS_TO_SHOW;
                    
                    return (
                        <div key={category} className={styles.integrationCategory}>
                            <div className={styles.categoryHeader}>
                                <Text className={styles.categoryTitle}>{category}</Text>
                                <Text className={styles.categoryCount}>
                                    {categoryModels.length} models available
                                </Text>
                            </div>
                            <div className={styles.modelsGrid}>
                                {displayedModels.map(model => (
                                    <Card key={model.id} className={styles.modelCard}>
                                        <div className={styles.modelHeader}>
                                            <img 
                                                src={model.icon} 
                                                alt={model.name}
                                                className={styles.modelIcon}
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                }}
                                            />
                                            <div className={styles.modelInfo}>
                                                <Text className={styles.modelName}>{model.name}</Text>
                                                <br />
                                                <Text className={styles.modelProvider}>{model.provider}</Text>
                                            </div>
                                            <Badge 
                                                appearance="filled" 
                                                color={model.isAvailable ? 'success' : 'subtle'}
                                            >
                                                {model.isAvailable ? 'Available' : 'Not Installed'}
                                            </Badge>
                                        </div>
                                        <Text className={styles.modelDescription}>{model.description}</Text>
                                        {model.assignedTo.length > 0 && (
                                            <div className={styles.assignedAgents}>
                                                <Text className={styles.assignedLabel}>Assigned to:</Text>
                                                <div className={styles.assignedList}>
                                                    {model.assignedTo.map((agent, index) => (
                                                        <Badge key={index} appearance="outline" className={styles.agentBadge}>
                                                            <span className={styles.agentBadgeIcon}>{getAgentIcon(agent)}</span> {agent}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </Card>
                                ))}
                            </div>
                            {hasMore && (
                                <Button 
                                    appearance="subtle" 
                                    className={styles.showMoreButton}
                                    onClick={() => toggleModelCategory(category)}
                                >
                                    {isExpanded ? 'Show less' : `Show ${remainingCount} more models`}
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Render Knowledge Base Tab
    const renderKnowledgeTab = () => {
        const knowledgeCategories = [...new Set(knowledgeBases.map(kb => kb.category))];
        const KB_TO_SHOW = 3;
        
        return (
            <div className={styles.tabContent}>
                <div className={styles.sectionHeader}>
                    <h1 className={styles.sectionTitle}>Knowledge Base (RAG)</h1>
                    <p className={styles.sectionDescription}>
                        Connect your document repositories and databases to enable AI-powered search and retrieval across your organization's knowledge.
                    </p>
                </div>
                
                {knowledgeCategories.map(category => {
                    const categoryKBs = knowledgeBases.filter(kb => kb.category === category);
                    const isExpanded = expandedKnowledgeCategories.has(category);
                    const displayedKBs = isExpanded 
                        ? categoryKBs 
                        : categoryKBs.slice(0, KB_TO_SHOW);
                    const hasMore = categoryKBs.length > KB_TO_SHOW;
                    const remainingCount = categoryKBs.length - KB_TO_SHOW;
                    
                    return (
                        <div key={category} className={styles.integrationCategory}>
                            <div className={styles.categoryHeader}>
                                <Text className={styles.categoryTitle}>{category}</Text>
                                <Text className={styles.categoryCount}>
                                    {categoryKBs.length} sources available
                                </Text>
                            </div>
                            <div className={styles.knowledgeGrid}>
                                {displayedKBs.map(kb => (
                                    <Card key={kb.id} className={styles.knowledgeCard}>
                                        <div className={styles.knowledgeHeader}>
                                            <img 
                                                src={kb.icon} 
                                                alt={kb.name}
                                                className={styles.knowledgeIcon}
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                }}
                                            />
                                            <div className={styles.knowledgeInfo}>
                                                <Text className={styles.knowledgeName}>{kb.name}</Text>
                                                <br />
                                                <Badge 
                                                    appearance="filled" 
                                                    color={kb.status === 'active' ? 'success' : 'subtle'}
                                                >
                                                    {kb.status}
                                                </Badge>
                                            </div>
                                            <Switch checked={kb.status === 'active'} />
                                        </div>
                                        <Text className={styles.knowledgeDescription}>{kb.description}</Text>
                                        <div className={styles.knowledgeStats}>
                                            <div className={styles.stat}>
                                                <Text className={styles.statValue}>{kb.documentCount.toLocaleString()}</Text>
                                                <Text className={styles.statLabel}>Documents</Text>
                                            </div>
                                            <div className={styles.stat}>
                                                <Text className={styles.statValue}>{kb.lastUpdated}</Text>
                                                <Text className={styles.statLabel}>Last Synced</Text>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                            {hasMore && (
                                <Button 
                                    appearance="subtle" 
                                    className={styles.showMoreButton}
                                    onClick={() => toggleKnowledgeCategory(category)}
                                >
                                    {isExpanded ? 'Show less' : `Show ${remainingCount} more sources`}
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Render Security & Access Tab
    const renderSecurityTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionHeader}>
                <h1 className={styles.sectionTitle}>Security & Access Control</h1>
                <p className={styles.sectionDescription}>
                    Manage user roles, authentication settings, and access policies for the Nolij platform. Infrastructure-level access is governed by GCP IAM in your GCP Console.
                </p>
            </div>

            {/* GCP IAM Banner */}
            <div style={{
                background: 'var(--colorNeutralBackground2)',
                border: '1px solid var(--colorNeutralStroke1)',
                borderLeft: '4px solid var(--colorBrandBackground)',
                borderRadius: 6,
                padding: '12px 16px',
                marginBottom: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 12
            }}>
                <span style={{ fontSize: 20 }}>🔒</span>
                <div>
                    <Text style={{ fontWeight: 600, fontSize: 13 }}>Two-Layer Access Control Active</Text>
                    <br />
                    <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>
                        Layer 1: GCP IAM (Solenis-managed) — controls VM, container, and Vertex AI access with MFA via GCP Cloud Identity.&nbsp;
                        Layer 2: Nolij RBAC (below) — controls application UI, domain access, and output visibility.
                    </Text>
                </div>
            </div>

            {/* RBAC - User Management */}
            <div className={styles.integrationCategory}>
                <div className={styles.categoryHeader}>
                    <Text className={styles.categoryTitle}>User Access Management (RBAC)</Text>
                    <Badge appearance="filled" color="success">Active</Badge>
                </div>

                {/* Role legend */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    <Badge appearance="filled" color="brand">Administrator</Badge>
                    <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)', alignSelf: 'center' }}>Full config, user mgmt, audit access</Text>
                    <Badge appearance="filled" color="informative" style={{ marginLeft: 12 }}>Domain User</Badge>
                    <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)', alignSelf: 'center' }}>Query + view assigned domains only</Text>
                    <Badge appearance="filled" color="subtle" style={{ marginLeft: 12 }}>Read-Only</Badge>
                    <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)', alignSelf: 'center' }}>View dashboards, no query submission</Text>
                </div>

                {/* User table */}
                <div style={{ overflowX: 'auto', border: '1px solid var(--colorNeutralStroke1)', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: 'var(--colorNeutralBackground2)', borderBottom: '2px solid var(--colorNeutralStroke1)' }}>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>User</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Role</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Domain Access</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>MFA Status</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Last Login</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { user: 'raman.marwah@solenis.com', role: 'Administrator', domain: 'All Domains', mfa: true, lastLogin: '2026-03-17 09:14 EST', active: true },
                                { user: 'finance.lead@solenis.com', role: 'Domain User', domain: 'Finance', mfa: true, lastLogin: '2026-03-17 08:52 EST', active: true },
                                { user: 'ap.analyst@solenis.com', role: 'Domain User', domain: 'Finance — AP Only', mfa: false, lastLogin: '2026-03-16 14:30 EST', active: true },
                                { user: 'order.ops@solenis.com', role: 'Domain User', domain: 'Order Management', mfa: true, lastLogin: '2026-03-17 07:45 EST', active: true },
                                { user: 'security.audit@solenis.com', role: 'Read-Only', domain: 'All Domains', mfa: true, lastLogin: '2026-03-15 11:20 EST', active: true },
                                { user: 'contractor.temp@solenis.com', role: 'Domain User', domain: 'Finance — AR Only', mfa: false, lastLogin: '2026-03-10 09:00 EST', active: false },
                            ].map((u, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid var(--colorNeutralStroke2)', background: i % 2 === 0 ? 'transparent' : 'var(--colorNeutralBackground2)' }}>
                                    <td style={{ padding: '10px 14px' }}>
                                        <Text style={{ fontSize: 13, fontWeight: 500 }}>{u.user}</Text>
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <Badge appearance="filled" color={u.role === 'Administrator' ? 'brand' : u.role === 'Read-Only' ? 'subtle' : 'informative'}>
                                            {u.role}
                                        </Badge>
                                    </td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--colorNeutralForeground2)' }}>{u.domain}</td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <Badge appearance={u.mfa ? 'filled' : 'outline'} color={u.mfa ? 'success' : 'warning'}>
                                            {u.mfa ? '🔐 Enrolled' : '⚠️ Not Enrolled'}
                                        </Badge>
                                    </td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, fontFamily: 'monospace', color: 'var(--colorNeutralForeground3)' }}>{u.lastLogin}</td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <Badge appearance="filled" color={u.active ? 'success' : 'subtle'}>
                                            {u.active ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <Button appearance="primary">+ Add User</Button>
                    <Button appearance="outline">Manage Roles</Button>
                    <Button appearance="subtle" style={{ marginLeft: 'auto' }}>Export Access Report</Button>
                </div>
            </div>

            {/* MFA Settings */}
            <div className={styles.integrationCategory}>
                <div className={styles.categoryHeader}>
                    <Text className={styles.categoryTitle}>Multi-Factor Authentication (MFA)</Text>
                </div>
                <div className={styles.settingsGrid}>
                    <Card className={styles.settingCard}>
                        <div className={styles.settingContent}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <Text className={styles.settingLabel}>Require MFA for Administrators</Text>
                                    <br />
                                    <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>All admin accounts must enroll in MFA</Text>
                                </div>
                                <Switch checked={true} />
                            </div>
                        </div>
                    </Card>
                    <Card className={styles.settingCard}>
                        <div className={styles.settingContent}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <Text className={styles.settingLabel}>Require MFA for All Users</Text>
                                    <br />
                                    <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>Enforce MFA across all user accounts</Text>
                                </div>
                                <Switch checked={false} />
                            </div>
                        </div>
                    </Card>
                    <Card className={styles.settingCard}>
                        <div className={styles.settingContent}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <Text className={styles.settingLabel}>SSO / Identity Provider</Text>
                                    <br />
                                    <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>GCP Cloud Identity (OIDC)</Text>
                                </div>
                                <Badge appearance="outline" color="informative">Roadmap</Badge>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Session & Access Policy */}
            <div className={styles.integrationCategory}>
                <div className={styles.categoryHeader}>
                    <Text className={styles.categoryTitle}>Session & Access Policy</Text>
                </div>
                <div className={styles.settingsGrid}>
                    <Card className={styles.settingCard}>
                        <div className={styles.settingContent}>
                            <Text className={styles.settingLabel}>Session Timeout</Text>
                            <br />
                            <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>Auto-logout after inactivity</Text>
                            <Input defaultValue="30 minutes" style={{ marginTop: 8, width: '100%' }} />
                        </div>
                    </Card>
                    <Card className={styles.settingCard}>
                        <div className={styles.settingContent}>
                            <Text className={styles.settingLabel}>Access Review Schedule</Text>
                            <br />
                            <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>Periodic review of all user access rights</Text>
                            <Input defaultValue="Quarterly" style={{ marginTop: 8, width: '100%' }} />
                        </div>
                    </Card>
                    <Card className={styles.settingCard}>
                        <div className={styles.settingContent}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <Text className={styles.settingLabel}>Field-Level Output Masking</Text>
                                    <br />
                                    <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>Hide sensitive fields from lower-privilege roles</Text>
                                </div>
                                <Switch checked={true} />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Periodic Access Review */}
            <div className={styles.integrationCategory}>
                <div className={styles.categoryHeader}>
                    <Text className={styles.categoryTitle}>Periodic Access Review</Text>
                    <Badge appearance="filled" color="warning">Review Due: Q2 2026</Badge>
                </div>
                <div style={{ overflowX: 'auto', border: '1px solid var(--colorNeutralStroke1)', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: 'var(--colorNeutralBackground2)', borderBottom: '2px solid var(--colorNeutralStroke1)' }}>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Review Period</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Reviewed By</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Users Reviewed</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Changes Made</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { period: 'Q1 2026 (Jan–Mar)', reviewer: 'raman.marwah@solenis.com', users: 6, changes: '1 account deactivated', status: 'Completed' },
                                { period: 'Q4 2025 (Oct–Dec)', reviewer: 'raman.marwah@solenis.com', users: 5, changes: 'No changes', status: 'Completed' },
                                { period: 'Q2 2026 (Apr–Jun)', reviewer: '—', users: '—', changes: '—', status: 'Upcoming' },
                            ].map((r, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid var(--colorNeutralStroke2)' }}>
                                    <td style={{ padding: '10px 14px', fontSize: 13 }}>{r.period}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--colorNeutralForeground2)' }}>{r.reviewer}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 13, textAlign: 'center' }}>{r.users}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>{r.changes}</td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <Badge appearance="filled" color={r.status === 'Completed' ? 'success' : 'warning'}>{r.status}</Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ marginTop: 12 }}>
                    <Button appearance="outline">Start Access Review</Button>
                    <Button appearance="subtle" style={{ marginLeft: 8 }}>Download Review Report</Button>
                </div>
            </div>

            {/* Service Account Configuration */}
            <div className={styles.integrationCategory}>
                <div className={styles.categoryHeader}>
                    <Text className={styles.categoryTitle}>Agent Service Accounts (GCP IAM)</Text>
                    <Text className={styles.categoryCount}>Provisioned by Solenis — credentials stored in GCP Secret Manager</Text>
                </div>
                <div style={{ overflowX: 'auto', border: '1px solid var(--colorNeutralStroke1)', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: 'var(--colorNeutralBackground2)', borderBottom: '2px solid var(--colorNeutralStroke1)' }}>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>System</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Service Account</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Permission Scope</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Managed By</th>
                                <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { system: 'Dynamics 365', account: 'nolij-d365-ap@solenis.iam', scope: 'Read — Invoices, POs, Vendors', managed: 'Solenis IAM', status: 'connected' },
                                { system: 'Oracle Fusion', account: 'nolij-oracle-ap@solenis.iam', scope: 'Read — AP/AR, GL', managed: 'Solenis IAM', status: 'connected' },
                                { system: 'GCP Vertex AI', account: 'nolij-vertex-ai@solenis.iam', scope: 'Inference only — no training access', managed: 'Solenis IAM', status: 'connected' },
                                { system: 'Salesforce', account: 'nolij-sf-crm@solenis.iam', scope: 'Read — Opportunities, Accounts', managed: 'Solenis IAM', status: 'disconnected' },
                                { system: 'SAP ERP', account: 'nolij-sap-fin@solenis.iam', scope: 'Read — Finance, Procurement', managed: 'Solenis IAM', status: 'disconnected' },
                            ].map((sa, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid var(--colorNeutralStroke2)', background: i % 2 === 0 ? 'transparent' : 'var(--colorNeutralBackground2)' }}>
                                    <td style={{ padding: '10px 14px', fontWeight: 500 }}>{sa.system}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, fontFamily: 'monospace', color: 'var(--colorNeutralForeground2)' }}>{sa.account}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>{sa.scope}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12 }}>{sa.managed}</td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <Badge appearance="filled" color={sa.status === 'connected' ? 'success' : 'subtle'}>
                                            {sa.status}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Text style={{ fontSize: 11, color: 'var(--colorNeutralForeground3)', marginTop: 8, display: 'block' }}>
                    * Service account permissions are verifiable in Solenis's GCP Console under IAM &amp; Admin → Service Accounts. Nolij holds no standing access to Solenis systems.
                </Text>
            </div>
        </div>
    );

    // Render Audit Log Tab
    const renderAuditTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionHeader}>
                <h1 className={styles.sectionTitle}>Audit Log</h1>
                <p className={styles.sectionDescription}>
                    Complete chain of custody for every agent action — from human query to system call to response. Logs are forwarded to GCP Cloud Logging.
                </p>
            </div>

            {/* Summary Stats */}
            <div className={styles.integrationCategory}>
                <div className={styles.settingsGrid} style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    {[
                        { label: 'Events Today', value: '1,284', color: 'var(--colorBrandForeground1)' },
                        { label: 'Active Users', value: '12', color: 'var(--colorPaletteGreenForeground1)' },
                        { label: 'System Calls', value: '847', color: 'var(--colorPaletteBlueForeground2)' },
                        { label: 'Alerts', value: '0', color: 'var(--colorPaletteRedForeground1)' },
                    ].map((stat, i) => (
                        <Card key={i} className={styles.settingCard} style={{ textAlign: 'center' }}>
                            <Text style={{ fontSize: 28, fontWeight: 700, color: stat.color }}>{stat.value}</Text>
                            <br />
                            <Text style={{ fontSize: 13, color: 'var(--colorNeutralForeground3)' }}>{stat.label}</Text>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Filters */}
            <div className={styles.integrationCategory}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    <Input placeholder="Search by user, system, or action..." style={{ flex: 1, minWidth: 240 }} />
                    <Button appearance="outline">Today</Button>
                    <Button appearance="subtle">Last 7 days</Button>
                    <Button appearance="subtle">Last 30 days</Button>
                    <Button appearance="primary" style={{ marginLeft: 'auto' }}>Export to CSV</Button>
                </div>

                {/* Audit Log Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid var(--colorNeutralStroke1)', textAlign: 'left' }}>
                                <th style={{ padding: '10px 12px', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Timestamp</th>
                                <th style={{ padding: '10px 12px', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>User</th>
                                <th style={{ padding: '10px 12px', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Event Type</th>
                                <th style={{ padding: '10px 12px', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>System Called</th>
                                <th style={{ padding: '10px 12px', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Action / Query</th>
                                <th style={{ padding: '10px 12px', color: 'var(--colorNeutralForeground3)', fontWeight: 600 }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { ts: '2026-03-17 09:14:32', user: 'raman.marwah@solenis.com', type: 'User Login', system: '—', action: 'Authenticated via application login', status: 'success' },
                                { ts: '2026-03-17 09:15:04', user: 'raman.marwah@solenis.com', type: 'Query Submitted', system: '—', action: '"Why is invoice NCF-2024-002 blocked?"', status: 'success' },
                                { ts: '2026-03-17 09:15:05', user: 'System Agent', type: 'MCP Tool Call', system: 'Dynamics 365', action: 'get_invoice(id="NCF-2024-002")', status: 'success' },
                                { ts: '2026-03-17 09:15:06', user: 'System Agent', type: 'MCP Tool Call', system: 'Oracle Fusion', action: 'get_purchase_order(po_number="PO-8821")', status: 'success' },
                                { ts: '2026-03-17 09:15:07', user: 'Research Agent', type: 'RAG Lookup', system: 'Knowledge Base', action: 'search_policy(query="vendor payment hold")', status: 'success' },
                                { ts: '2026-03-17 09:15:09', user: 'System Agent', type: 'Agent Response', system: '—', action: 'Analysis returned to user — 3 systems queried', status: 'success' },
                                { ts: '2026-03-17 08:52:11', user: 'finance.lead@solenis.com', type: 'Query Submitted', system: '—', action: '"Show all invoices pending approval over $50K"', status: 'success' },
                                { ts: '2026-03-17 08:52:12', user: 'System Agent', type: 'MCP Tool Call', system: 'Oracle Fusion', action: 'list_invoices(status="pending", min_amount=50000)', status: 'success' },
                                { ts: '2026-03-17 08:45:00', user: 'System', type: 'Batch Run', system: 'All AP Systems', action: 'Nightly batch analysis — 142 invoices processed', status: 'success' },
                                { ts: '2026-03-16 17:30:22', user: 'ap.analyst@solenis.com', type: 'Query Submitted', system: '—', action: '"What is the aging status of vendor V-4421?"', status: 'success' },
                            ].map((row, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid var(--colorNeutralStroke2)', background: i % 2 === 0 ? 'transparent' : 'var(--colorNeutralBackground2)' }}>
                                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>{row.ts}</td>
                                    <td style={{ padding: '10px 12px', fontSize: 12 }}>{row.user}</td>
                                    <td style={{ padding: '10px 12px' }}>
                                        <Badge appearance="outline" color={
                                            row.type === 'MCP Tool Call' ? 'brand' :
                                            row.type === 'User Login' ? 'informative' :
                                            row.type === 'Batch Run' ? 'warning' :
                                            'subtle'
                                        }>{row.type}</Badge>
                                    </td>
                                    <td style={{ padding: '10px 12px', fontSize: 12 }}>{row.system}</td>
                                    <td style={{ padding: '10px 12px', fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.action}</td>
                                    <td style={{ padding: '10px 12px' }}>
                                        <Badge appearance="filled" color="success">{row.status}</Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: 12, color: 'var(--colorNeutralForeground3)' }}>Showing 10 of 1,284 events today · Logs forwarded to GCP Cloud Logging</Text>
                    <Button appearance="subtle">Load more</Button>
                </div>
            </div>
        </div>
    );

    // Render Advanced Tab
    const renderAdvancedTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionHeader}>
                <h1 className={styles.sectionTitle}>Advanced Settings</h1>
                <p className={styles.sectionDescription}>
                    Configure system-level settings, API connections, and debugging options for your multi-agent environment.
                </p>
            </div>
            
            <div className={styles.advancedSection}>
                {/* API Configuration */}
                <div className={styles.integrationCategory}>
                    <div className={styles.categoryHeader}>
                        <Text className={styles.categoryTitle}>API Configuration</Text>
                    </div>
                    <div className={styles.settingsGrid}>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>🔗</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Backend URL</Text>
                                <Input defaultValue="http://localhost:8000" className={styles.settingInput} />
                            </div>
                        </div>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>📡</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>WebSocket URL</Text>
                                <Input defaultValue="ws://localhost:8000/ws" className={styles.settingInput} />
                            </div>
                        </div>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>⏱️</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Request Timeout (seconds)</Text>
                                <Input defaultValue="30" type="number" className={styles.settingInput} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Agent Behavior */}
                <div className={styles.integrationCategory}>
                    <div className={styles.categoryHeader}>
                        <Text className={styles.categoryTitle}>Agent Behavior</Text>
                    </div>
                    <div className={styles.settingsGrid}>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>🧠</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Show Agent Reasoning</Text>
                                <Text className={styles.settingDescription}>Display step-by-step thinking process</Text>
                            </div>
                            <Switch defaultChecked />
                        </div>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>✅</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Require Approval for Actions</Text>
                                <Text className={styles.settingDescription}>Ask before executing financial operations</Text>
                            </div>
                            <Switch defaultChecked />
                        </div>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>🔄</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Auto-retry Failed Tasks</Text>
                                <Text className={styles.settingDescription}>Automatically retry on transient errors</Text>
                            </div>
                            <Switch />
                        </div>
                    </div>
                </div>

                {/* Logging & Debug */}
                <div className={styles.integrationCategory}>
                    <div className={styles.categoryHeader}>
                        <Text className={styles.categoryTitle}>Logging & Debug</Text>
                    </div>
                    <div className={styles.settingsGrid}>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>🐛</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Enable Debug Mode</Text>
                                <Text className={styles.settingDescription}>Show detailed logs and diagnostics</Text>
                            </div>
                            <Switch />
                        </div>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>📝</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Log Level</Text>
                                <Input defaultValue="INFO" className={styles.settingInput} />
                            </div>
                        </div>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>💾</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Persist Conversation History</Text>
                                <Text className={styles.settingDescription}>Save chat history to database</Text>
                            </div>
                            <Switch defaultChecked />
                        </div>
                    </div>
                </div>

                {/* Performance */}
                <div className={styles.integrationCategory}>
                    <div className={styles.categoryHeader}>
                        <Text className={styles.categoryTitle}>Performance</Text>
                    </div>
                    <div className={styles.settingsGrid}>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>⚡</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Max Concurrent Agents</Text>
                                <Input defaultValue="4" type="number" className={styles.settingInput} />
                            </div>
                        </div>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>📊</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Cache RAG Results</Text>
                                <Text className={styles.settingDescription}>Cache document retrieval for faster responses</Text>
                            </div>
                            <Switch defaultChecked />
                        </div>
                        <div className={styles.settingCard}>
                            <div className={styles.settingIcon}>🗑️</div>
                            <div className={styles.settingContent}>
                                <Text className={styles.settingLabel}>Cache TTL (minutes)</Text>
                                <Input defaultValue="60" type="number" className={styles.settingInput} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Suppress unused variable warnings
    void setModels;
    void setKnowledgeBases;

    return (
        <div className={styles.settingsPage}>
            {/* Header */}
            <div className={styles.header}>
                <Button 
                    appearance="subtle" 
                    icon={<ArrowLeft24Regular />}
                    onClick={handleBack}
                    className={styles.backButton}
                >
                    Back
                </Button>
                <Text className={styles.pageTitle}>Settings</Text>
            </div>

            {/* Main Content */}
            <div className={styles.mainContent}>
                {/* Sidebar Navigation */}
                <div className={styles.sidebar}>
                    <TabList
                        vertical
                        selectedValue={activeTab}
                        onTabSelect={handleTabSelect}
                        className={styles.tabList}
                    >
                        <Tab value="teams" icon={<People24Regular />} className={styles.tab}>
                            Teams
                        </Tab>
                        <Tab value="integrations" icon={<PlugConnected24Regular />} className={styles.tab}>
                            Integrations
                        </Tab>
                        <Tab value="models" icon={<Bot24Regular />} className={styles.tab}>
                            AI Models
                        </Tab>
                        <Tab value="knowledge" icon={<Library24Regular />} className={styles.tab}>
                            Knowledge Base
                        </Tab>
                        <Tab value="advanced" icon={<Settings24Regular />} className={styles.tab}>
                            Advanced
                        </Tab>
                        <Tab value="security" icon={<ShieldLock24Regular />} className={styles.tab}>
                            Security & Access
                        </Tab>
                        <Tab value="audit" icon={<ClipboardDataBar24Regular />} className={styles.tab}>
                            Audit Log
                        </Tab>
                    </TabList>
                </div>

                {/* Content Area */}
                <div className={styles.contentArea}>
                    {activeTab === 'teams' && renderTeamsTab()}
                    {activeTab === 'integrations' && renderIntegrationsTab()}
                    {activeTab === 'models' && renderModelsTab()}
                    {activeTab === 'knowledge' && renderKnowledgeTab()}
                    {activeTab === 'advanced' && renderAdvancedTab()}
                    {activeTab === 'security' && renderSecurityTab()}
                    {activeTab === 'audit' && renderAuditTab()}
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
